import classNames from "classnames";
import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useLocation, useParams } from "react-router-dom";

import {
    fetchDriversAndTires,
    fetchRaceResultsByCircuit,
    fetchQualifyingResultsByCircuit,
    fetchLocationData,
} from "../utils/api.js";
import { fetchOpenF1Json } from "../config/openf1";
import { organizeQualifyingResults } from "../utils/organizeQualifyingResults.js";

import {
    DriverCard,
    Loading,
    ThreeCanvas,
    LapChart,
    TireStrategy,
    StartingGrid,
    SelectedDriverStats,
    FastestLaps,
    PositionCharts,
    Tabs,
    ReactSelectComponent,
    Modal,
} from "../components";
import Drawer from "../components/Drawer.js";
import Accordion from "../components/Accordion.js";
import { locationMaps } from "../utils/locationMaps.js";
import { fas } from "@fortawesome/free-solid-svg-icons";

export function RacePage() {
    const { state } = useLocation();
    const { raceId } = useParams();
    const [raceName, setRaceName] = useState(state ? state.raceName : null);
    const [meetingKey, setMeetingKey] = useState(
        state ? state.meetingKey : raceId
    );
    const [year, setYear] = useState(state ? state.year : null);
    const [location, setLocation] = useState(state ? state.location : null);
    const [drivers, setDrivers] = useState([]);
    const [laps, setLaps] = useState([]);
    const [pos, setPos] = useState([]);
    const [driversDetails, setDriversDetails] = useState({});
    const [driverSelected, setDriverSelected] = useState(false);
    const [driverCode, setDriverCode] = useState("");
    const [driverNumber, setDriverNumber] = useState("");
    const [driversColor, setDriversColor] = useState({});
    const [startingGrid, setStartingGrid] = useState([]);
    const [animatedMap, setAnimatedMap] = useState("");
    const [MapPath, setMapPath] = useState("");
    const [raceResults, setRaceResults] = useState([]);
    const [locData, setLocData] = useState({});
    const [activeButtonIndex, setActiveButtonIndex] = useState(null);
    const [startTime, setStartTime] = useState("");
    const [endTime, setEndTime] = useState("");
    const [speedFactor, setSpeedFactor] = useState(0.2);
    const [isPaused, setIsPaused] = useState(false);
    const [haloView, setHaloView] = useState(false);
    const [topFollowView, setTopFollowView] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedSession, setSelectedSession] = useState("Race");
    const [selectedSessionKey, setSelectedSessionKey] = useState('');
    const [selectedSessionData, setSelectedSessionData] = useState(null);
    const [hasRaceSession, sethasRaceSession] = useState(false);
    const [hasQualifyingSession, sethasQualifyingSession] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [driverDrawerOpen, setDriverDrawerOpen] = useState(false);
    const [showStartingGrid, setShowStartingGrid] = useState(false);
    const [showCarDetails, setShowCarDetails] = useState(true);
    const [showCameraControls, setShowCameraControls] = useState(false);
    const [activeStatsTab, setActiveStatsTab] = useState("");
    const [loadedStatsData, setLoadedStatsData] = useState({
        drivers: false,
        position: false,
        laps: false,
        tires: false,
    });

    useEffect(() => {
        const setBaseData = async () => {
            setRaceName(state.raceName);
            setYear(state.year);
            setLocation(state.location);
            setMeetingKey(state.meetingKey);
        };
        if (state) setBaseData();
    }, [state]);

    useEffect(() => {
        const fetchByMeetingKey = async () => {
            setIsLoading(true);
            const response = await fetch(
                `https://praneeth7781.github.io/f1nsight-api-2/races/racesbyMK.json`
            ).then((res) => res.json());
            setYear(response[raceId]["year"]);
            setLocation(response[raceId]["location"]);
            setRaceName(response[raceId]["raceName"]);
            // console.log(raceName);
        };

        if (raceName) {
        } else {
            fetchByMeetingKey();
            fetchData();
        }

        const handleResize = () => {
            setShowStartingGrid(window.innerWidth > 640 && true);
        };
        window.addEventListener("resize", handleResize);
        // Cleanup event listener on unmount
        return () => window.removeEventListener("resize", handleResize);

    }, []);

    const animatedLocations = [
        "Austin",
        "Bahrain",
        "Baku",
        "Budapest",
        "Jeddah",
        "Las Vegas",
        "Lusail",
        "Marina Bay",
        "Melbourne",
        "Mexico City",
        "Miami",
        "Monaco",
        "Monza",
        "São Paulo",
        "Sakhir",
        "Shanghai",
        "Silverstone",
        "Spa-Francorchamps",
        "Suzuka",
        "Yas Island",
        "Zandvoort",
    ];

    const selectedDriverData = drivers.find(
        (obj) => obj["acronym"] === driverCode
    );
    const selectedDriverRaceData = raceResults.find(
        (obj) => String(obj["number"]) === String(driverNumber)
    );

    const getPositionTimeBounds = (positionData, sessionData) => {
        if (!positionData.length) {
            return {
                startTime: sessionData?.date_start || "",
                endTime: sessionData?.date_end || "",
            };
        }

        const snapshotDate = positionData[0]?.date;
        const firstDifferentDate = positionData.find(
            (item) => item.date !== snapshotDate
        )?.date;

        if (firstDifferentDate) {
            const date = new Date(firstDifferentDate);

            if (!Number.isNaN(date.getTime())) {
                date.setMinutes(date.getMinutes() - 1);
                return {
                    startTime: date.toISOString(),
                    endTime:
                        positionData[positionData.length - 1]?.date ||
                        sessionData?.date_end ||
                        snapshotDate ||
                        "",
                };
            }
        }

        return {
            startTime: sessionData?.date_start || snapshotDate || "",
            endTime:
                sessionData?.date_end ||
                positionData[positionData.length - 1]?.date ||
                snapshotDate ||
                "",
        };
    };

    const safeFetch = async (label, request, fallback = []) => {
        try {
            return await request;
        } catch (error) {
            console.warn(`Optional race page data failed: ${label}`, error);
            return fallback;
        }
    };

    const setTrackAssets = (circuitId) => {
        if (!circuitId) {
            setMapPath("");
            setAnimatedMap("");
            console.warn(`No track asset mapped for location: ${location}`);
            return;
        }

        setMapPath(`${process.env.PUBLIC_URL + "/map/" + circuitId + ".gltf"}`);
        setAnimatedMap(
            `${
                process.env.PUBLIC_URL +
                "/mapsAnimated/" +
                circuitId +
                "Animated.mp4"
            }`
        );
    };

    const resetOpenF1StatsData = () => {
        setDrivers([]);
        setLaps([]);
        setPos([]);
        setDriversDetails({});
        setDriversColor({});
        setStartingGrid([]);
        setSelectedSessionData(null);
        setLoadedStatsData({
            drivers: false,
            position: false,
            laps: false,
            tires: false,
        });
    };

    const loadDriverDetails = async (sessionKey, force = false) => {
        if (!sessionKey || (!force && loadedStatsData.drivers)) return driversDetails;

        const driverDetailsData = await safeFetch(
            "drivers",
            fetchOpenF1Json("/drivers", { session_key: sessionKey })
        );

        const driverDetailsMap = driverDetailsData.reduce(
            (acc, driver) => ({
                ...acc,
                [driver.driver_number]: driver.name_acronym,
            }),
            {}
        );

        const driverColorMap = driverDetailsData.reduce(
            (acc, driver) => ({
                ...acc,
                [driver.name_acronym]: driver.team_colour,
            }),
            {}
        );

        setDriversDetails(driverDetailsMap);
        setDriversColor(driverColorMap);
        setLoadedStatsData((prev) => ({ ...prev, drivers: true }));
        return driverDetailsMap;
    };

    const loadPositionData = async (sessionKey, sessionData, force = false) => {
        if (!sessionKey || (!force && loadedStatsData.position)) return;

        const driverDetailsMap = await loadDriverDetails(sessionKey, force);
        const [positionData, lapsData] = await Promise.all([
            safeFetch(
                "position",
                fetchOpenF1Json("/position", { session_key: sessionKey })
            ),
            safeFetch(
                "laps for position chart",
                fetchOpenF1Json("/laps", { session_key: sessionKey })
            ),
        ]);

        setPos(positionData);
        setLaps(
            lapsData.map((lap) => ({
                ...lap,
                driver_acronym: driverDetailsMap[lap.driver_number],
            }))
        );

        const { startTime, endTime } = getPositionTimeBounds(
            positionData,
            sessionData
        );
        setStartTime(startTime);
        setEndTime(endTime);

        const earliestDateTime = positionData[0]?.date;
        setStartingGrid(
            positionData.filter((item) => item.date === earliestDateTime)
        );
        setLoadedStatsData((prev) => ({
            ...prev,
            position: true,
            laps: true,
        }));
    };

    const loadLapData = async (sessionKey) => {
        if (!sessionKey || loadedStatsData.laps) return;

        const driverDetailsMap = await loadDriverDetails(sessionKey);
        const lapsData = await safeFetch(
            "laps",
            fetchOpenF1Json("/laps", { session_key: sessionKey })
        );

        setLaps(
            lapsData.map((lap) => ({
                ...lap,
                driver_acronym: driverDetailsMap[lap.driver_number],
            }))
        );
        setLoadedStatsData((prev) => ({ ...prev, laps: true }));
    };

    const loadTireData = async (sessionKey) => {
        if (!sessionKey || loadedStatsData.tires) return;

        const driversData = await safeFetch(
            "drivers and tires",
            fetchDriversAndTires(sessionKey)
        );
        setDrivers(driversData);
        setLoadedStatsData((prev) => ({ ...prev, tires: true }));
    };

    const fetchData = async () => {
        if (!raceName) return;

        try {
            setDriverSelected(false);
            setActiveButtonIndex(null);
            setIsLoading(true);
            resetOpenF1StatsData();

            const circuitId = locationMaps[location];
            setTrackAssets(circuitId);
            const sessionsData = await safeFetch(
                "sessions",
                fetchOpenF1Json("/sessions", {
                    meeting_key: meetingKey,
                })
            );

            const hasRaceSession = sessionsData.some(
                (session) => session.session_name === "Race"
            );
            sethasRaceSession(hasRaceSession);
            const hasQualifyingSession = sessionsData.some(
                (session) => session.session_name === "Qualifying"
            );
            sethasQualifyingSession(hasQualifyingSession);

            if (selectedSession === "Race") {
                setIsLoading(true);

                if (circuitId) {
                    const results = await safeFetch(
                        "race results",
                        fetchRaceResultsByCircuit(year, circuitId)
                    );
                    setRaceResults(results);
                    // console.log(results);
                }

                const raceSession = sessionsData.find(
                    (session) => session.session_name === "Race"
                );
                if (!raceSession) {
                    console.warn("Race session not found");
                    return;
                }
                const sessionKey = raceSession.session_key;
                setSelectedSessionKey(sessionKey);
                setSelectedSessionData(raceSession);
                setStartTime(raceSession.date_start || "");
                setEndTime(raceSession.date_end || "");
                setActiveStatsTab("position");
                await loadPositionData(sessionKey, raceSession, true);

                setIsLoading(false);
            } else if (selectedSession === "Qualifying") {
                setIsLoading(true);

                if (circuitId) {
                    const results = await safeFetch(
                        "qualifying results",
                        fetchQualifyingResultsByCircuit(year, circuitId)
                    );
                    setRaceResults(results);
                    // console.log(results);
                }

                const raceSession = sessionsData.find(
                    (session) => session.session_name === "Qualifying"
                );
                if (!raceSession) {
                    console.warn("Qualifying session not found");
                    return;
                }
                const sessionKey = raceSession.session_key;
                setSelectedSessionKey(sessionKey);
                setSelectedSessionData(raceSession);
                setStartTime(raceSession.date_start || "");
                setEndTime(raceSession.date_end || "");
                setActiveStatsTab("laps");

                setIsLoading(false);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [year, location, selectedSession, raceName]);

    useEffect(() => {
        if (!selectedSessionKey || !activeStatsTab) return;

        if (activeStatsTab === "position") {
            loadPositionData(selectedSessionKey, selectedSessionData);
            return;
        }

        if (activeStatsTab === "laps") {
            loadLapData(selectedSessionKey);
            return;
        }

        if (activeStatsTab === "tires" || activeStatsTab === "fastest") {
            loadTireData(selectedSessionKey);
        }
    }, [activeStatsTab, selectedSessionKey, selectedSessionData]);

    const handleDriverSelectionClick = async (index) => {
        // console.log(raceResults[index].Driver.code); // Log the driver code
        // console.log(raceResults[index].number);

        if (activeButtonIndex === index) {
            setLocData({});
            setDriverSelected(false);
            setActiveButtonIndex(null); // Reset the active button index
            setDriverCode("");
        } else {
            await loadTireData(selectedSessionKey);
            setLocData({});
            setDriverSelected(true);
            setDriverCode(raceResults[index].Driver.code);
            setDriverNumber(raceResults[index].number);
            setActiveButtonIndex(index); // Set new active button index

            (async () => {
                try {
                    // Fetch sessions to find the race session
                    const sessionsData = await fetchOpenF1Json("/sessions", {
                        meeting_key: meetingKey,
                    });
                    const raceSession = sessionsData.find(
                        (session) => session.session_name === "Race"
                    );
                    if (!raceSession) throw new Error("Race session not found");
                    const sessionKey = raceSession.session_key;

                    const scaleFactor = 1500;

                    // Fetch location data using sessionKey, driverId (from state), startTime, and endTime
                    const locationData = await fetchLocationData(
                        sessionKey,
                        raceResults[index].number,
                        startTime,
                        endTime,
                        scaleFactor
                    );
                    setLocData(locationData);
                } catch (error) {
                    console.error("Error fetching location data:", error);
                }
            })();
        }
    };

    const DriverList = ({
        results,
        activeButtonIndex,
        handleDriverSelectionClick,
        drivers,
        driversColor,
        year,
        session,
    }) => {
        return (
            <ul className="w-fit mx-auto">
                {results.map((result, index) => (
                    <DriverCard
                        hasHover={false}
                        isActive={activeButtonIndex === index}
                        index={index}
                        driver={result.Driver}
                        stint={drivers}
                        driverColor={driversColor[result.Driver.code]} // Use driver code here
                        startPosition={parseInt(result.grid, 10)}
                        endPosition={parseInt(result.position, 10)}
                        year={parseInt(year)}
                        time={result[session]}
                        fastestLap={result.FastestLap}
                        layoutSmall={index > 2}
                        mobileSmall
                        isRace={false}
                    />
                ))}
            </ul>
        );
    };

    // console.log(location, MapPath);
    // console.log({raceResults});
    // console.log({startingGrid});

    const { q1Results, q2Results, q3Results } =
        organizeQualifyingResults(raceResults);

    // console.log('Q1 Results:', q1Results);
    // console.log('Q2 Results:', q2Results);
    // console.log('Q3 Results:', q3Results);

    // console.log('selectedSession', selectedSession);

    const driverSelectedShowTrack =
        (driverSelected && animatedLocations.includes(location)) ||
        !animatedLocations.includes(location);

    const driverButtons = (layoutSmall) => (
        <ul className="flex flex-col p-16">
            {raceResults.map((result, index) => (
                <button
                    key={index}
                    className="block w-full mb-2 relative"
                    onClick={() => {
                        handleDriverSelectionClick(index);
                        setModalOpen(false);
                        setIsDrawerOpen(false);
                    }}
                >
                    <DriverCard
                        hasHover
                        isActive={activeButtonIndex === index}
                        index={index}
                        driver={result.Driver}
                        stint={drivers}
                        driverColor={driversColor[driverCode]}
                        startPosition={parseInt(result.grid, 10)}
                        endPosition={parseInt(result.position, 10)}
                        year={parseInt(year)}
                        time={result.Time?.time || result.status}
                        fastestLap={result.FastestLap}
                        layoutSmall={layoutSmall}
                        // mobileSmall
                        isRace={true}
                    />
                </button>
            ))}
        </ul>
    )

    const driver1LapData = {
        driverNumber: '14',  // Driver 1's number (e.g., Piastri)
        lap: 3,             // The lap number
        sessionKey: selectedSessionKey,  // The session key of the race
        fastestLapTime: "1:39.256",
      };
      
      const driver2LapData = {
        driverNumber: '4',   // Driver 2's number (e.g., Norris)
        lap: 53,             // The lap number
        sessionKey: selectedSessionKey,  // The session key of the race
        fastestLapTime: "1:35.454",
      };

    const selectedDriverAcronym = driverSelected
        ? driversDetails[driverNumber]
        : null;
    const statsTabs = [
        selectedSession === "Race" && {
            id: "position",
            label: "Position",
            content: (
                <PositionCharts
                    laps={laps}
                    pos={pos}
                    startGrid={startingGrid}
                    driversDetails={driversDetails}
                    driversColor={driversColor}
                    raceResults={raceResults}
                    driverCode={selectedDriverAcronym}
                />
            ),
        },
        {
            id: "laps",
            label: "Lap Chart",
            content: (
                <LapChart
                    laps={laps}
                    setLaps={() => setLaps}
                    driversDetails={driversDetails}
                    driversColor={driversColor}
                    raceResults={raceResults}
                    className="lap-chart"
                    driverCode={selectedDriverAcronym}
                />
            ),
        },
        {
            id: "tires",
            label: "Tire Strategy",
            content: (
                <TireStrategy
                    drivers={drivers}
                    raceResults={raceResults}
                    driverCode={selectedDriverAcronym}
                    driverColor={driversColor[driverCode]}
                />
            ),
        },
        !driverSelected &&
            selectedSession === "Race" && {
                id: "fastest",
                label: "Fastest Laps",
                content: (
                    <FastestLaps raceResults={raceResults} drivers={drivers} />
                ),
            },
    ].filter(Boolean);

    //   console.log('selectedSessionKey:', selectedSessionKey);
    return isLoading ? (
        <Loading
            className="mt-[20rem] mb-[20rem]"
            message={`Loading ${raceName} ${year} ${selectedSession}`}
        />
    ) : (
        <div className="race-page">
            <div className="race-page__track-view relative md:pt-64">
                <div className="absolute bottom-8 w-full flex justify-between sm:justify-end items-center z-10 gap-8 px-8">
                    {driverSelected && (
                        <div className="flex items-center gap-8">
                            <button
                                className={classNames(
                                    "race-controls__play bg-glow w-32 h-32 rounded-sm",
                                    {
                                        "bg-plum-500": !isPaused,
                                    }
                                )}
                                onClick={() => setIsPaused(false)}
                            >
                                <FontAwesomeIcon icon="play" />
                            </button>
                            <button
                                className={classNames(
                                    "race-controls__pause bg-glow w-32 h-32 rounded-sm",
                                    {
                                        "bg-plum-500": isPaused,
                                    }
                                )}
                                onClick={() => setIsPaused(true)}
                            >
                                <FontAwesomeIcon icon="pause" />
                            </button>
                            <button 
                                className={classNames(
                                    "bg-glow w-32 h-32 rounded-sm",
                                    {
                                        "bg-plum-500": showCameraControls,
                                    }
                                )}
                                onClick={() => setShowCameraControls(!showCameraControls)}
                            >
                                <FontAwesomeIcon icon="camera-rotate" />
                            </button>
                            <button 
                                className={classNames(
                                    "bg-glow w-32 h-32 rounded-sm",
                                    {
                                        "bg-plum-500": showCarDetails,
                                    }
                                )}
                                onClick={() => setShowCarDetails(!showCarDetails)}
                            >
                                <FontAwesomeIcon icon="gauge" />
                            </button>
                        </div>
                        
                    )}
                    <div className="flex items-center gap-8">
                        <button 
                            className="bg-glow w-32 h-32 rounded-sm sm:hidden"
                            onClick={() => setDriverDrawerOpen(true)}
                        >
                            <FontAwesomeIcon icon="user" />
                        </button>
                        <button 
                            className="bg-glow w-32 h-32 rounded-sm"
                            onClick={() => setIsDrawerOpen(true)}
                        >
                            <FontAwesomeIcon icon="gear" />
                        </button>                        
                    </div>
                </div>

                <Drawer isOpen={driverDrawerOpen} onClose={() => setDriverDrawerOpen(false)}>
                    <div className="w-full tracking-xs text-center text-neutral-300 py-24 leading-none">
                        Select driver from the leaderboard to activate race mode
                    </div>
                    {driverButtons(true)}
                </Drawer>
                <Drawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)}>
                    {driverSelected && (
                        <>
                        <Accordion title="Playback Speed" contentClasses="flex flex-col gap-8 items-start">
                            <button
                                className={classNames(
                                    "tracking-sm uppercase block",
                                    {
                                        "text-plum-300":
                                            speedFactor !== 4,
                                    }
                                )}
                                onClick={() => {
                                    setSpeedFactor(4)
                                    setIsDrawerOpen(false)
                                }}
                            >
                                Normal
                            </button>
                            <button
                                className={classNames(
                                    "tracking-sm uppercase block",
                                    {
                                        "text-plum-300":
                                            speedFactor !== 1.5,
                                    }
                                )}
                                onClick={() => {
                                    setSpeedFactor(1.5)
                                    setIsDrawerOpen(false)
                                }}
                            >
                                Push Push
                            </button>
                            <button
                                className={classNames(
                                    "tracking-sm uppercase block",
                                    {
                                        "text-plum-300":
                                            speedFactor !== 0.2,
                                    }
                                )}
                                onClick={() => {
                                    setSpeedFactor(0.2)
                                    setIsDrawerOpen(false)
                                }}
                            >
                                DRS
                            </button>
                        </Accordion>
                        <Accordion title="Camera Angle" contentClasses="flex flex-col gap-8 items-start">
                            <button
                                className={classNames(
                                    "tracking-sm uppercase block",
                                    {
                                        "text-plum-300": !haloView || !topFollowView,
                                    }
                                )}
                                onClick={() => {
                                    setHaloView(false)
                                    setTopFollowView(false)
                                    setIsDrawerOpen(false)
                                }}
                            >
                                Sky View
                            </button>
                            <button
                                className={classNames(
                                    "tracking-sm uppercase block",
                                    {
                                        "text-plum-300": haloView,
                                    }
                                )}
                                onClick={() => {
                                    setHaloView(true)
                                    setTopFollowView(false)
                                    setIsDrawerOpen(false)
                                }}
                            >
                                Halo View
                            </button>
                            <button
                                className={classNames(
                                    "tracking-sm uppercase block",
                                    {
                                        "text-plum-300": topFollowView,
                                    }
                                )}
                                onClick={() => {
                                    setTopFollowView(true)
                                    setIsDrawerOpen(false)
                                }}
                            >
                                Top Follow View
                            </button>
                        </Accordion>
                        </>
                    )}
                    <Accordion title="Race Selection" contentClasses="flex flex-col gap-8 items-start">
                        {hasRaceSession && (
                            <button
                                className={classNames(
                                    "tracking-sm uppercase block",
                                    {
                                        "text-plum-300": selectedSession === 'Race',
                                    }
                                )}
                                onClick={() => {
                                    setSelectedSession("Race")
                                    setIsDrawerOpen(false)
                                }}
                            >
                                Race
                            </button>
                        )}
                        {hasQualifyingSession && (
                            <button
                                className={classNames(
                                    "tracking-sm uppercase block",
                                    {
                                        "text-plum-300": selectedSession === 'Qualifying',
                                    }
                                )}
                                onClick={() => {
                                    setSelectedSession("Qualifying")
                                    setIsDrawerOpen(false)
                                }}
                            >
                                Qualifying
                            </button>
                        )}
                    </Accordion>
                </Drawer>

                {selectedSession === "Race" && (
                    <>
                    <div className="bg-glow-dark text-center py-8 max-sm:hidden">Select a driver from the leaderboard to activate telemetry viewer</div>
                    <div className="race-page__track-view__display relative">
                        {driverSelectedShowTrack ? (
                            <ThreeCanvas
                                className="race-page__track-view__display__canvas"
                                MapFile={MapPath}
                                locData={locData}
                                driverSelected={driverSelected}
                                constructorId={selectedDriverRaceData?.Constructor?.constructorId || ""}
                                driverCode={driverCode}
                                driverColor={driversColor[driverCode]}
                                isPaused={isPaused}
                                haloView={haloView}
                                topFollowView={topFollowView}
                                speedFactor={speedFactor}
                                year={year}
                                showCarDetails={showCarDetails}
                                showCameraControls={showCameraControls}
                            />
                        ) : (
                            <div className="race-page__track-view__display__preview">
                                <video
                                    src={animatedMap}
                                    loop
                                    autoPlay
                                    muted
                                    playsInline
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        )}
                        <div className="max-sm:hidden absolute top-[0] left-[0]">
                            {driverButtons(true)}
                        </div>
                    </div>
                    </>
                )}
            </div>
            {/* End .race-page__track-view */}

            <div className="race-page__scroll-container">
                {selectedSession === "Qualifying" && (
                    <button className="text-xs tracking-xs uppercase mb-16 bg-glow rounded-sm p-4 ml-8" onClick={() => setSelectedSession("Race")}>
                        <FontAwesomeIcon icon="chevron-left" className="mr-16" />
                        race
                    </button>
                )}

                <div className="mb-40 flex flex-col gap-4 items-center uppercase">
                    <p className="text-sm tracking-sm">{year}</p> 
                    <h1 className="heading-3">{raceName}</h1> 
                    {selectedSession === "Qualifying" && <p className="text-sm tracking-sm">{selectedSession}</p>}
                    <div className="divider-glow-dark mt-32" />
                </div>

                {/* Qualifying View */}
                {selectedSession !== "Race" && (
                    <div className="flex items-start justify-center gap-8 sm:gap-32 mx-8 mb-32">
                        <div className="p-16 bg-glow-dark rounded-md sm:rounded-xlarge max-md:w-full">
                            <h3 className="heading-3 mb-32 gradient-text-light">
                                Q1
                            </h3>
                            <DriverList
                                results={q1Results}
                                activeButtonIndex={activeButtonIndex}
                                handleDriverSelectionClick={
                                    handleDriverSelectionClick
                                }
                                drivers={drivers}
                                driversColor={driversColor}
                                year={year}
                                session="Q1"
                            />
                        </div>
                        <div className="p-16 bg-glow-dark rounded-md sm:rounded-xlarge max-md:w-full">
                            <h3 className="heading-3 mb-32 gradient-text-light">
                                Q2
                            </h3>
                            <DriverList
                                results={q2Results}
                                activeButtonIndex={activeButtonIndex}
                                handleDriverSelectionClick={
                                    handleDriverSelectionClick
                                }
                                drivers={drivers}
                                driversColor={driversColor}
                                year={year}
                                session="Q2"
                            />
                        </div>
                        <div className="p-16 bg-glow-dark rounded-md sm:rounded-xlarge max-md:w-full">
                            <h3 className="heading-3 mb-32 gradient-text-light">
                                Q3
                            </h3>
                            <DriverList
                                results={q3Results}
                                activeButtonIndex={activeButtonIndex}
                                handleDriverSelectionClick={
                                    handleDriverSelectionClick
                                }
                                drivers={drivers}
                                driversColor={driversColor}
                                year={year}
                                session="Q3"
                            />
                        </div>
                    </div>
                )}

                {/* Raace View */}
                <div className="page-container-centered flex flex-col justify-center sm:flex-row gap-16 mt-32">
                    {selectedSession === "Race" && (
                        <div className="sm:w-[26rem]">
                            {driverSelected &&
                                selectedDriverData &&
                                selectedDriverRaceData && (
                                <SelectedDriverStats
                                    selectedDriverData={selectedDriverData}
                                    selectedDriverRaceData={
                                        selectedDriverRaceData
                                    }
                                    year={year}
                                />
                            )}
                            <div 
                                className={classNames(
                                    "flex flex-row justify-between gap-4 max-sm:mb-16",
                                )}
                            >
                                <button 
                                    className={classNames(
                                        "text-neutral-400 font-display sm:ml-24 sm:text-xl sm:mb-16 leading-none", 
                                        showStartingGrid && "max-sm:text-white"
                                    )} 
                                    onClick={() => setShowStartingGrid(true)}
                                >
                                    Starting Grid
                                </button>
                                <button 
                                    className={classNames("text-neutral-400 font-display sm:hidden", !showStartingGrid && "text-white")}
                                    onClick={() => setShowStartingGrid(false)}
                                >
                                    Race Results
                                </button>
                            </div>
                            <div className={classNames("flex flex-row items-start gap-4 sm:hidden mb-24")}>
                                <StartingGrid
                                    className={classNames("transition-all overflow-hidden", showStartingGrid ? "w-2/3" : "w-1/3 opacity-15")}
                                    raceResults={raceResults}
                                    startingGrid={startingGrid}
                                    year={year}
                                    driverCode={driverCode}
                                    driversDetails={driversDetails}
                                    driversColor={driversColor}
                                />
                                <div 
                                    className={classNames(
                                        "bg-glow-large h-fit rounded-md sm:rounded-xlarge transition-all overflow-hidden",
                                        showStartingGrid ? "w-1/3 opacity-15" : "w-2/3"
                                    )}>
                                    {driverButtons(false)}
                                </div>
                            </div>
                            <StartingGrid
                                className={classNames("max-sm:hidden w-[26rem]")}
                                raceResults={raceResults}
                                startingGrid={startingGrid}
                                year={year}
                                driverCode={driverCode}
                                driversDetails={driversDetails}
                                driversColor={driversColor}
                            />
                        </div>
                    )}

                    <div className="sm:grow">
                        <Tabs
                            tabs={statsTabs}
                            activeTabId={activeStatsTab}
                            onTabChange={setActiveStatsTab}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
